import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Column, DataTable } from "@/components/ui/data-table";
import { useSorting } from "@/hooks/useSorting";
import { cn } from "@/lib/utils";
import {
  formatServiceSchedule,
  type ServiceScheduleInput,
} from "@/components/pages/services/utils/serviceSchedule";
import type { PartnerHotelCurationItemSource } from "@/types/partner-destination";
import { GuesteriaCurationFiltersPopover } from "@/components/pages/guesteria-partners/components/GuesteriaCurationFiltersPopover";
import {
  countActiveCurationFilters,
  DEFAULT_CURATION_FILTERS,
  type CurationFiltersState,
} from "@/components/pages/guesteria-partners/utils/guesteriaCurationFilters";
import {
  filterActivityTableRows,
  sortActivityRows,
  type ActivityStatusFilter,
  type ActivityTableRow,
} from "@/components/pages/guesteria-partners/utils/guesteriaActivitiesTable";

export type GuesteriaActivityRow = ActivityTableRow &
  ServiceScheduleInput & {
    source: PartnerHotelCurationItemSource;
    price: number | null;
    /** `true`: added by the hotel (can be removed); `false`: city list item. */
    addedByHotel: boolean;
  };

export type GuesteriaActivitiesTableProps = {
  rows: GuesteriaActivityRow[];
  canEdit: boolean;
  onToggleHidden: (productCode: string, hidden: boolean) => void;
  onRemove: (productCode: string) => void;
};

const STATUS_OPTIONS: ActivityStatusFilter[] = ["all", "active", "hidden"];

export const GuesteriaActivitiesTable = ({
  rows,
  canEdit,
  onToggleHidden,
  onRemove,
}: GuesteriaActivitiesTableProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { sortField, sortDirection, handleSort } = useSorting();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ActivityStatusFilter>("all");

  const [curationFilters, setCurationFilters] = useState<CurationFiltersState>(
    DEFAULT_CURATION_FILTERS,
  );

  const filtersActive =
    search.trim() !== "" ||
    status !== "all" ||
    countActiveCurationFilters(curationFilters) > 0;

  const visibleRows = useMemo(
    () =>
      sortActivityRows(
        filterActivityTableRows(rows, search, status, curationFilters),
        sortField,
        sortDirection,
      ),
    [rows, search, status, curationFilters, sortField, sortDirection],
  );

  const columns: Column<GuesteriaActivityRow>[] = useMemo(
    () => [
      {
        key: "name",
        header: t("partners.curation.table.name"),
        sortable: true,
        className: "flex-1",
        render: (_v, row) => (
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-[#171717]">
              {row.title}
            </span>
          </div>
        ),
      },
      {
        key: "location",
        header: t("partners.curation.table.location"),
        sortable: true,
        className: "w-[160px]",
        render: (_v, row) => (
          <span className="text-sm text-[#171717]">{row.location || "—"}</span>
        ),
      },
      {
        key: "top",
        header: t("partners.curation.table.top"),
        sortable: true,
        className: "w-[80px] text-center",
        render: (_v, row) => (
          <span className="text-sm text-[#171717]">{row.position}</span>
        ),
      },
      {
        key: "schedule",
        header: t("partners.curation.table.schedule"),
        sortable: false,
        className: "w-[170px]",
        render: (_v, row) => (
          <span className="text-sm text-[#171717]">
            {formatServiceSchedule(
              row,
              t("services.activity.common.na", "N/A"),
            )}
          </span>
        ),
      },
      {
        key: "status",
        header: t("partners.curation.table.status"),
        sortable: true,
        className: "w-[140px] text-center",
        render: (_v, row) => (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
              row.hidden
                ? "bg-gray-50 text-gray-700"
                : "bg-green-50 text-green-700",
            )}
          >
            {row.hidden
              ? t("partners.curation.hidden")
              : t("partners.curation.visibleToGuests")}
          </span>
        ),
      },
      {
        key: "actions",
        header: t("partners.curation.table.actions"),
        sortable: false,
        className: "w-[120px] text-center",
        render: (_v, row) =>
          row.addedByHotel ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canEdit}
              onClick={() => onRemove(row.productCode)}
            >
              {t("partners.curation.remove")}
            </Button>
          ) : (
            <Switch
              checked={!row.hidden}
              disabled={!canEdit}
              onCheckedChange={(checked) =>
                onToggleHidden(row.productCode, !checked)
              }
              aria-label={t("partners.curation.visibleToGuests")}
            />
          ),
      },
    ],
    [t, canEdit, onToggleHidden, onRemove],
  );

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setCurationFilters(DEFAULT_CURATION_FILTERS);
  };

  const emptyMessage =
    rows.length === 0
      ? t("partners.curation.table.empty")
      : t("partners.curation.filters.noMatches");

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("partners.curation.table.searchPlaceholder")}
          aria-label={t("partners.curation.table.searchPlaceholder")}
          className="h-8 w-64"
        />
        <div
          className="flex items-center gap-1"
          role="group"
          aria-label={t("partners.curation.table.status")}
        >
          {STATUS_OPTIONS.map((option) => (
            <Button
              key={option}
              type="button"
              size="sm"
              variant={status === option ? "default" : "outline"}
              aria-pressed={status === option}
              onClick={() => setStatus(option)}
            >
              {t(`partners.curation.table.statusOptions.${option}`)}
            </Button>
          ))}
        </div>
        <GuesteriaCurationFiltersPopover
          applied={curationFilters}
          onApply={setCurationFilters}
        />
        {filtersActive && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={resetFilters}
          >
            {t("partners.curation.filters.reset")}
          </Button>
        )}
      </div>
      <DataTable<GuesteriaActivityRow>
        data={visibleRows}
        columns={columns}
        getRowKey={(row) =>
          `${row.addedByHotel ? "a" : "c"}-${row.productCode}`
        }
        columnHeaderClassName="text-xs font-semibold bg-gray-50"
        sorting={{
          field: sortField,
          direction: sortDirection,
          onSort: handleSort,
        }}
        emptyMessage={emptyMessage}
        withPagination={false}
      />
    </div>
  );
};
